import { Alert, Badge, Kpi, Module, ModuleBody, ModuleHead, PageFrame, StatTile, StatusDot } from "@vjal/ui";
import { DataTableCollection } from "@vjal/ui";
import type { DataTableColumn } from "@vjal/ui";
import { Link } from "react-router-dom";
import { RouteActionButton } from "../components/RouteActionButton";
import type { DashboardReadModel, DashboardReadSourceFamily } from "../domain/dashboard-read-model";
import { getRoleDefinition, type CoachingRole } from "../domain/roles";
import { useCoachingDashboardReadModel } from "../hooks/useCoachingQueries";
import { isLiveCoachingDataMode } from "../hooks/useLiveProductData";
import { humanizeStatus } from "../lib/status-format";
import { useWalkReadyMarker } from "../lib/walk-readiness";
import { ParticipantDashboardPage } from "./participant-dashboard/participant-dashboard-page";
import type { ProductScreenContext, ProductScreenContract, TableRow } from "./product-route-contracts";

type DashboardTone = "success" | "warning" | "info";

interface DashboardMetric {
  label: string;
  value: string;
  meta: string;
}

interface DashboardRow extends TableRow {
  priority: string;
  item: string;
  owner: string;
  status: string;
  tone: DashboardTone;
}

interface DashboardConfig {
  title: string;
  summary: string;
  alert: string;
  metrics: DashboardMetric[];
  rows: DashboardRow[];
  primaryAction: string;
  primaryPath: string;
  secondaryAction: string;
  secondaryPath: string;
}

const dashboardConfigs: Record<CoachingRole, DashboardConfig> = {
  super_admin: {
    title: "Global operations and permissions",
    summary: "Global operating view with permission readiness separated from normal admin work.",
    alert: "Permission templates, view-as, and impersonation are not available yet.",
    metrics: [
      { label: "Companies", value: "1", meta: "global active" },
      { label: "Cohorts", value: "2", meta: "active and draft" },
      { label: "Sessions", value: "10", meta: "scheduled scope" },
      { label: "Upcoming features", value: "3", meta: "not available yet" }
    ],
    rows: [
      row("permissions", "Permission templates", "Super admin", "coming soon", "warning"),
      row("history", "Global event history", "Audit trail", "redacted summaries", "info"),
      row("reports", "Report review", "Artifacts", "coming soon", "warning")
    ],
    primaryAction: "Open permissions gate",
    primaryPath: "/permissions",
    secondaryAction: "Review global history",
    secondaryPath: "/history"
  },
  admin: {
    title: "Operational command center",
    summary: "Global operations, cohorts needing action, upcoming sessions, reports, and recent event history.",
    alert: "Normal admins can create coaches and operational users, but cannot grant admin or super admin roles.",
    metrics: [
      { label: "Cohorts needing action", value: "2", meta: "capacity or invite risk" },
      { label: "Upcoming sessions", value: "10", meta: "next 30 days" },
      { label: "Invite gaps", value: "1", meta: "delivery pending" },
      { label: "Reports ready", value: "1", meta: "review gated" }
    ],
    rows: [
      row("cohort-1", "Spring Leaders", "Admin", "active cohort", "success"),
      row("session-1", "Session 1 invites", "Coach One", "delivery pending", "warning"),
      row("report-1", "Session 1 Report", "Artifacts", "ready", "success")
    ],
    primaryAction: "Create cohort",
    primaryPath: "/cohorts/new",
    secondaryAction: "Review users",
    secondaryPath: "/users"
  },
  coach: {
    title: "Assigned coaching work",
    summary: "Assigned active cohorts, upcoming sessions, invite state, scoped reports, and scoped activity.",
    alert: "Unassigned companies, cohorts, sessions, participants, reports, and history are absent from this dashboard.",
    metrics: [
      { label: "Assigned cohorts", value: "1", meta: "Spring Leaders" },
      { label: "Upcoming sessions", value: "10", meta: "assigned schedule" },
      { label: "Invite gaps", value: "1", meta: "delivery pending" },
      { label: "Scoped reports", value: "1", meta: "ready for review" }
    ],
    rows: [
      row("cohort-1", "Spring Leaders", "Coach One", "assigned active", "success"),
      row("session-1", "Session 1", "Coach One", "invite follow-up", "warning"),
      row("report-1", "Session 1 Report", "Coach One", "scoped report", "info")
    ],
    primaryAction: "Create series",
    primaryPath: "/cohorts/cohort-1/sessions/series",
    secondaryAction: "Bulk import",
    secondaryPath: "/cohorts/cohort-1/participants/bulk"
  },
  company_admin: {
    title: "Assigned company status",
    summary: "Read-only company dashboard for active records, sessions, reports, resources, and history summaries.",
    alert: "Company admin is read-only and sees exactly the assigned company status path.",
    metrics: [
      { label: "Assigned company", value: "1", meta: "Acme Leadership" },
      { label: "Active cohorts", value: "1", meta: "current company" },
      { label: "Session status", value: "10", meta: "read-only" },
      { label: "Reports", value: "1", meta: "assigned company" }
    ],
    rows: [
      row("company-1", "Acme Leadership", "Company admin", "assigned company", "success"),
      row("session-1", "Session 1 invite status", "Coach One", "read-only status", "info"),
      row("history-1", "Recent company history", "Audit trail", "redacted summary", "info")
    ],
    primaryAction: "Open resources",
    primaryPath: "/resources",
    secondaryAction: "Update profile",
    secondaryPath: "/settings/profile"
  },
  participant: {
    title: "Learner home",
    summary: "Own cohorts, upcoming meetings, progress, invite status, own reports, and global resources.",
    alert: "Participant dashboard shows own records only; no operational history, transcripts, or peer private fields render.",
    metrics: [
      { label: "Own cohorts", value: "2", meta: "active learning paths" },
      { label: "Upcoming meetings", value: "1", meta: "own schedule" },
      { label: "Progress", value: "4/10", meta: "Spring Leaders" },
      { label: "Reports", value: "1", meta: "own report ready" }
    ],
    rows: [
      row("cohort-1", "Spring Leaders", "Learner One", "4 of 10 sessions", "success"),
      row("cohort-4", "Summer Managers", "Learner One", "2 of 8 sessions", "info"),
      row("session-1", "Session 1 Zoom and calendar", "Learner One", "own invite accepted", "success")
    ],
    primaryAction: "Open resources",
    primaryPath: "/resources",
    secondaryAction: "Update profile",
    secondaryPath: "/settings/profile"
  },
  cohort_lead: {
    title: "Cohort lead read-only view",
    summary: "Assigned cohorts with progress, attendance, assignments, and report downloads only; no delivery controls render.",
    alert: "Cohort lead access is view and report-download only for assigned cohorts; mutation controls are hidden.",
    metrics: [
      { label: "Assigned cohorts", value: "1", meta: "view only" },
      { label: "Progress", value: "4/10", meta: "Spring Leaders" },
      { label: "Reports", value: "1", meta: "download available" },
      { label: "Mutations", value: "0", meta: "not available to leads" }
    ],
    rows: [
      row("cohort-1", "Spring Leaders", "Cohort lead", "assigned read-only", "info"),
      row("report-1", "Session 1 Report", "Artifacts", "download available", "success"),
      row("attendance-1", "Weekly attendance", "Reports", "view only", "info")
    ],
    primaryAction: "Open cohorts",
    primaryPath: "/cohorts",
    secondaryAction: "Open reports",
    secondaryPath: "/reports"
  }
};

export function dashboardScreen(): ProductScreenContract {
  return {
    title: (role) => `${getRoleDefinition(role).label} dashboard`,
    primaryJob:
      "Start from role-specific priorities: global operations, assigned coaching work, company status, or learner progress.",
    secondaryJob: "Everything here is scoped to your role, so you only see the work that's yours.",
    workTitle: "Role dashboard priorities",
    rows: dashboardConfigs.admin.rows,
    columns: dashboardColumns(),
    states: ["loading", "empty", "error", "read-only", "role-specific", "approved-composed-read", "stale-data-prevention"],
    apiFamilies: ["dashboard", "cohort-service", "session-service", "artifact-service", "history-service"],
    roleDeltas:
      "Super admin sees permission readiness; admin sees global operations; coach sees assigned work; company_admin and participant see read-only status or learner paths.",
    extensionSlots: ["dashboard read models", "permission alerts", "invite risk", "learner progress", "company status widgets"],
    mobilePosture: "Role metrics, next-work table, and read-only action explanations stack without horizontal overflow.",
    evidenceRequirement: "Playwright must prove all five role dashboard variants on desktop and mobile.",
    vjalComponents: ["Alert","Badge","CollectionPagination","CollectionToolbar","DataTableCollection","DataTableV2","IconCalendar","IconDoc","Kpi","Module","ModuleBody","ModuleHead","PageFrame","StatTile","StatusDot"],
    localCompositions: ["dashboard focused product surface", "RouteActionButton"],
    decisionHierarchy: {
      firstViewport: "Role-specific metrics and scope summary.",
      primaryAction: "Move into the safest next product path for the current role.",
      secondaryPanel: "Prioritized scoped work table.",
      deferredSurface: "Richer live dashboard metrics arrive as more of your workspace data is connected.",
      gatedControls: "Read-only roles see disabled mutation controls with accessible reasons."
    },
    requiredViewports: ["desktop", "mobile"],
    closureState: "focused",
    closureOwner: "VIL-20260608-0026",
    surfaceMode: "focused",
    customBody: DashboardSurface
  };
}

// Wave 9: a PARTICIPANT lands on the dedicated Participant Dashboard (their
// complete self-service home), not the generic role-dashboard composition. This
// is the ONLY surface a participant sees; its isolation + report-exclusion gates
// are enforced inside the projection (own-data-only, no report read). The router
// picks the right SURFACE COMPONENT so each component's hooks stay unconditional.
function DashboardSurface(context: ProductScreenContext) {
  if (context.role === "participant") {
    return <ParticipantDashboardPage {...context} />;
  }
  return <RoleDashboardSurface {...context} />;
}

function RoleDashboardSurface({ role }: ProductScreenContext) {
  const live = isLiveCoachingDataMode();
  const config = dashboardConfigs[role];
  const readModel = useCoachingDashboardReadModel(role);
  // Walk-readiness marker, phase 2 (see src/lib/walk-readiness.ts). The route
  // shell delegates the flip to this surface because the composed read is still
  // in flight when the shell settles — flipping there would harvest the shimmer
  // tiles and the pre-read badge copy. Every settled branch is terminal: data,
  // empty, and the honest "Dashboard read unavailable" error posture all render
  useWalkReadyMarker(!readModel.isLoading);
  const model = readModel.data;
  const liveMetrics = live ? (model?.metrics ?? []) : null;
  const visibleRows = live ? liveDashboardRows(model) : config.rows;
  const dashboardSummary = live && model && model.totals ? dashboardSummaryLabel(model, visibleRows.length) : null;
  const deniedFamilies = (live && model?.deniedFamilies) || [];

  return (
    <PageFrame title={config.title} titleAs={2} subtitle={config.summary} className="col-span-12 min-w-0 max-w-full">
      <Module span={12} className="min-w-0">
        <ModuleHead title={config.title} subtitle={config.summary} />
        <ModuleBody className="grid min-w-0 gap-4 md:grid-cols-2 xl:grid-cols-4">
          {live ? (
            // LIVE: shimmer StatTiles while loading, honest "—" empty tiles on a
            // fresh org or a failed read (never a fabricated zero), values when
            // ingested. The bare loading sentence is replaced by the shimmer row.
            <DashboardStatTiles
              metrics={liveMetrics}
              loading={readModel.isLoading}
              empty={readModel.isError || model?.state === "empty"}
              emptyLabel={readModel.isError ? "Unavailable right now — try again shortly" : "Will populate once your workspace has data"}
            />
          ) : (
            // DEMO/mock composition: illustrative Kpi tiles (not a fresh live org).
            config.metrics.map((metric) => <Kpi key={metric.label} label={metric.label} value={metric.value} meta={metric.meta} />)
          )}
        </ModuleBody>
      </Module>
      <Module span={8} className="min-w-0">
        <ModuleHead title="Priority work" subtitle="Showing only the records assigned to you." />
        <ModuleBody className="min-w-0 space-y-4" flush={!live}>
          {live && deniedFamilies.length > 0 ? (
            <Alert variant="info">
              {`The live service denied ${deniedFamilies.length} of ${model?.sourceFamilies.length ?? 6} dashboard read areas for this role${

                model?.supportRef ? ` (ref ${model.supportRef})` : ""
              }. Denied areas stay hidden instead of rendering placeholder records.`}
            </Alert>
          ) : null}
          {dashboardSummary ? <p className="m-0 text-sm leading-6 text-ink-muted">{dashboardSummary}</p> : null}
          {live && model ? <DashboardFamilyLinks role={role} model={model} /> : null}
          <div
            className="min-w-0 max-w-full overflow-x-auto"
            aria-label="Scrollable priority work"
            data-testid="dashboard-priority-scroll"
          >
            <DataTableCollection caption={`${getRoleDefinition(role).label} dashboard priorities`} columns={dashboardColumns()} data={visibleRows} stableChipNames />
          </div>
        </ModuleBody>
      </Module>
      <Module span={4} className="min-w-0">
        <ModuleHead title="Quick actions" subtitle="Your next steps in this workspace." />
        <ModuleBody className="space-y-4">
          <Alert variant="info">{config.alert}</Alert>
          <RouteActionButton label={config.primaryAction} path={config.primaryPath} role={role} />
          <RouteActionButton label={config.secondaryAction} path={config.secondaryPath} role={role} variant="secondary" />
          <Badge variant={readModel.isError ? "warning" : "vjal"}>
            {model
              ? deniedFamilies.length > 0
                ? "Scope-limited dashboard summary"
                : dashboardContractLabel(model.contractKind)
              : readModel.isError
                ? "Dashboard read unavailable"
                : "Scoped to your role"}
          </Badge>
          <p className="m-0 text-sm leading-6 text-ink-muted">
            {readModel.isError
              ? "Dashboard data is unavailable; previous rows stay hidden until the service recovers."
              : dashboardScopeSummary(role)}
          </p>
          {model ? (
            <p className="m-0 text-sm leading-6 text-ink-muted">
              {model.staleDataRendered
                ? `Some of your ${model.sourceFamilies.length} data areas may be out of date.`
                : `All ${model.sourceFamilies.length} of your data areas are up to date.`}
            </p>
          ) : null}
        </ModuleBody>
      </Module>
    </PageFrame>
  );
}

// KPI stat-tile row for the dashboard hero (Admin + Participant). Honest by
// construction: `loading` renders shimmer tiles, `empty` renders "—" + an
// explainer (never a fabricated zero), and otherwise the ingested value shows.
// A fresh org / failed read therefore NEVER invents a number.
interface StatTileMetric {
  label: string;
  value: string;
  meta: string;
}

function DashboardStatTiles({
  metrics,
  loading,
  empty,
  emptyLabel
}: {
  metrics: StatTileMetric[] | null;
  loading: boolean;
  empty: boolean;
  emptyLabel: string;
}) {
  // Stable 4-up skeleton while the read model is in flight.
  if (loading || !metrics || metrics.length === 0) {
    const slots = metrics && metrics.length > 0 ? metrics : DASHBOARD_STAT_SKELETON;
    return (
      <>
        {slots.map((slot, index) => (
          <StatTile
            key={slot.label ? `${slot.label}-${index}` : `dashboard-stat-${index}`}
            label={slot.label || "Loading"}
            value="—"
            loading={loading}
            empty={!loading}
            emptyLabel={emptyLabel}
          />
        ))}
      </>
    );
  }

  return (
    <>
      {metrics.map((metric, index) => (
        <StatTile
          key={`${metric.label}-${index}`}
          label={metric.label}
          value={metric.value}
          meta={metric.meta}
          empty={empty}
          emptyLabel={emptyLabel}
        />
      ))}
    </>
  );
}

const DASHBOARD_STAT_SKELETON: StatTileMetric[] = [
  { label: "", value: "", meta: "" },
  { label: "", value: "", meta: "" },
  { label: "", value: "", meta: "" },
  { label: "", value: "", meta: "" }
];
const DASHBOARD_PRIORITY_LIMIT = 12;


function liveDashboardRows(model: DashboardReadModel | undefined): DashboardRow[] {
  if (!model) return [];
  return model.rows.slice(0, DASHBOARD_PRIORITY_LIMIT).map((row) => ({
    id: `${row.sourceFamily}:${row.id}`,
    priority: "Status",
    item: row.label,
    owner: row.sourceFamily,
    status: row.status,
    tone: "info" as const
  }));
}

function dashboardSummaryLabel(model: DashboardReadModel, visibleCount: number) {
  const totals = model.totals ? Object.values(model.totals) : [undefined];
  const knownTotal = totals.reduce<number>((sum, total) => sum + (total ?? 0), 0);
  const unknownTotal = totals.some((total) => total === undefined);
  const totalLabel = unknownTotal ? "12+" : String(knownTotal);
  return `Showing ${visibleCount} of ${totalLabel}`;
}

function dashboardContractLabel(contractKind: string) {
  return contractKind === "approved-composed-read" ? "Scoped to your role" : "Dashboard summary";
}

const DASHBOARD_FAMILY_ROUTES: Record<DashboardReadSourceFamily, string> = {
  "company-service": "/companies",
  "cohort-service": "/cohorts",
  "session-service": "/sessions",
  "artifact-service": "/reports",
  "resource-service": "/resources",
  "history-service": "/history"
};


const DASHBOARD_FAMILY_LABELS: Record<DashboardReadSourceFamily, string> = {
  "company-service": "Companies",
  "cohort-service": "Cohorts",
  "session-service": "Sessions",
  "artifact-service": "Reports",
  "resource-service": "Resources",
  "history-service": "History"
};

const DASHBOARD_ALLOWED_FAMILIES: Record<CoachingRole, DashboardReadSourceFamily[]> = {
  super_admin: ["company-service", "cohort-service", "session-service", "artifact-service", "resource-service", "history-service"],
  admin: ["company-service", "cohort-service", "session-service", "artifact-service", "resource-service", "history-service"],
  coach: ["company-service", "cohort-service", "session-service", "artifact-service", "resource-service", "history-service"],
  company_admin: ["company-service", "cohort-service", "session-service", "artifact-service", "resource-service", "history-service"],
  participant: ["cohort-service"],
  cohort_lead: ["cohort-service", "session-service", "artifact-service", "resource-service", "history-service"]
};

function DashboardFamilyLinks({ role, model }: { role: CoachingRole; model: DashboardReadModel }) {
  const families = model.sourceFamilies.filter((family) => DASHBOARD_ALLOWED_FAMILIES[role].includes(family));
  return (
    <nav aria-label="Full dashboard lists" className="flex flex-wrap gap-x-3 gap-y-1 text-sm">
      {families.map((family) => (
        <Link key={family} to={dashboardFamilyRoute(role, family)} className="text-link no-underline hover:underline">
          {DASHBOARD_FAMILY_LABELS[family]}
        </Link>
      ))}
    </nav>
  );
}

function dashboardFamilyRoute(role: CoachingRole, family: DashboardReadSourceFamily) {
  if (family === "cohort-service" && (role === "coach" || role === "participant")) return "/my-cohorts";
  return DASHBOARD_FAMILY_ROUTES[family];
}

function dashboardScopeSummary(role: CoachingRole) {
  if (role === "super_admin") return "Global operations are visible while permission-management features stay backend-gated.";
  if (role === "admin") return "Operational records are visible without admin or super-admin grant escalation.";
  if (role === "coach") return "Only assigned companies, cohorts, sessions, reports, resources, and history are shown.";
  if (role === "company_admin") return "Exactly one assigned company is shown with read-only status records.";
  return "Only learner-owned cohorts, sessions, reports, and shared resources are shown.";
}

function row(id: string, item: string, owner: string, status: string, tone: DashboardTone): DashboardRow {
  return {
    id,
    priority: tone === "warning" ? "Needs review" : tone === "success" ? "On track" : "Status",
    item,
    owner,
    status,
    tone
  };
}

function dashboardColumns(): DataTableColumn<TableRow>[] {
  return [
    { key: "priority", header: "Priority", render: (row) => <StatusDot tone={row.tone === "success" ? "ok" : "info"} label={String(row.priority)} /> },
    { key: "item", header: "Item" },
    { key: "owner", header: "Owner" },
    { key: "status", header: "Status", render: (row) => <Badge variant={row.tone === "warning" ? "warning" : row.tone === "success" ? "success" : "info"}>{humanizeStatus(String(row.status))}</Badge> }
  ];
}
